/**
 * Team page CD · FRAME 4 + frame 4's slice of FRAME 5 — THE ONE PLACE THE
 * ATHLETE SURFACE'S STRINGS LIVE.
 *
 * ⚠️ STRINGS ARE LAW. Every constant here is a `COPY-LEDGER.md` row and ships
 * CHARACTER-FOR-CHARACTER — em dashes (—), middots (·), the `›` disclosure
 * chevron and the `→` link arrow included. The open picks render their
 * RECOMMENDED candidate (column A); they are PROVISIONAL pending the founder's
 * screenshot review, not a build invention. `§D1` is PICKED and is quoted here
 * exactly as picked.
 *
 * ⛔ BANNED ON THIS SURFACE, PERMANENTLY (frame 5 voice of record + the ledger's
 * standing constraints): exclamation marks · the word `safe` · `on track` · any
 * health framing · any pressuring line · any performance verdict about an
 * athlete · any aggregate of kudos RECEIVED · any ranking of a person.
 * `athleteTeamCopy.test.ts` (beside this file) asserts all of it over every
 * EXPORT here, which is why the file exists at all rather than the strings
 * sitting inline. ⚠️ That is a MODULE lint, and its blind spot is everything
 * the athlete tree renders from somewhere else — `<Roster>`, `<KudosFeed>`,
 * `<KudosComposer>`, a legacy fallback. `athlete-rendered-lint.test.tsx` closes
 * it by running the same assertion set over the RENDERED output of every
 * harness state; neither test is sufficient alone.
 *
 * ⚠️ SINGULARS ARE PROVISIONAL, on frame 1's precedent
 * (`lib/roster/coachHomeCopy.ts`). The ledger writes its rows in the plural
 * (`{n} runners`, `You sent {g} kudos this week.`), and a ledger string ships
 * character-for-character — so a team of one and a week of one kudo have no
 * ratified string. Rendering `1 runners` is worse than centralising one
 * provisional variant that is a single edit from whatever the founder picks.
 * ⛔ Do not inline an `n === 1 ? … : …` anywhere in the frame-4 components.
 */

/** The plural-aware core. `one` is the provisional variant; `many` is ledgered. */
function pick(n: number, one: string, many: string): string {
  return n === 1 ? one : many;
}

// ── §2.1 · THE TEAM PULSE HEADER ────────────────────────────────────────────

/** §C1 — pulse meta. Ledgered: `{n} runners · {season}`. Athlete register says
 *  *runners*; the coach register says *athletes* (frame 4 §5). */
export function pulseMetaLabel(n: number, season: string): string {
  return `${n} ${pick(n, 'runner', 'runners')} · ${season}`;
}

/** §C2 — the presence line. Ledgered: `{n} teammates trained this week.`
 *  D4.5: a count of PEOPLE, in no order, with no names attached to numbers and
 *  no comparison to the reader. Presence, never ranking. */
export function presenceLine(n: number): string {
  return `${n} ${pick(n, 'teammate', 'teammates')} trained this week.`;
}

/** §C3 — the presence line when nobody has logged a run yet this week.
 *  ⛔ ONLY valid when the count is KNOWN to be zero. It must never stand in for
 *  "we could not find out" — that is an all-clear the data cannot support
 *  (frame 5's second added voice rule). See `AthleteTeamTab.tsx` §2.1. */
export const PRESENCE_LINE_NONE = 'No runs logged by the team this week.';

/** §C4 — coach identity. Ledgered: `Coached by {Name}`. Identity only, no link:
 *  the human coach is not a chat target (Airo is). */
export function coachedByLabel(name: string): string {
  return `Coached by ${name}`;
}

/**
 * §D1 — THE §9c DISCOVERABILITY DISCLOSURE (contract §4.8, build-blocking).
 * AMENDED 2026-08-02 (CD rulings 1–5) · PRESENTATION AMENDED 2026-08-20
 * (`cd-2026-08-20-d1-presentation/CD-RULINGS-2026-08-20-D1-PRESENTATION.md`,
 * R41–R49).
 *
 * ⚖️ FOUR-PART ANATOMY (R42, on the 2026-08-02 three-slot structure):
 *
 *   S0 · the index line  → `DISCOVERABILITY_INDEX_LINE` — ALWAYS VISIBLE, first.
 *                          Names every capability class in the record, one clause
 *                          per record sentence, each clause assembled from that
 *                          sentence's OWN ratified opening words; the location
 *                          clause is ALWAYS TERMINAL (the emphatic slot). The
 *                          inline trigger (`DISCOVERABILITY_TRIGGER_LABEL`)
 *                          renders at S0's tail.
 *   the record (folded)  → S1 register · S3 location · S4 mileage · S5
 *                          runs+effort, verbatim, slot order, behind ONE
 *                          in-place fold. Folded by default on every mount.
 *   S2 · the consent fence → `DISCOVERABILITY_CONSENT_LINE` — NEVER FOLDS,
 *                          always visible, and S2 ENDS THE RECORD (the C27
 *                          pin, re-worded by R59: no record sentence may
 *                          ever land below S2). Folding the consent fence
 *                          would re-bury exactly what ruling 1 lifted out
 *                          of S1's tail.
 *   the location stratum → BELOW S2, after a 1px `--ink-100` hairline
 *                          (CD 2026-08-21 R59/R60, dark behind
 *                          `race_disc_stratum_v1`). LIVE STATE, not record
 *                          prose — the one §D1 element that changes at
 *                          runtime, which is why it sits OUTSIDE the record
 *                          and never interleaves with it. NEVER inside the
 *                          fold (S11: a fold hides elaboration, never
 *                          existence, never a CONTROL — the stratum carries
 *                          the live `End sharing` button). The fold opens
 *                          ABOVE it; fold state and share state are fully
 *                          independent. Strings: the DISC set below +
 *                          ARM-12 (`ENDED_BAR_LINE`, reused from
 *                          `lib/run/raceLiveArm.ts` — a declared reuse,
 *                          never a retype).
 *
 * ⚖️ S11 (standing): a fold may hide ELABORATION, never EXISTENCE — the
 * always-visible layer must name every category its folded record carries.
 * A pointer that names no categories ("details ›") is a burial, not a fold.
 *
 * ⚖️ GROWTH RULE (R48, superseding "appends as a new line" ONLY in render
 * position — the append discipline itself is unchanged). A new capability (S6)
 * lands TWICE in ONE PR (the RB-7 discipline):
 *   · its ratified sentence appends to the record BEFORE S2, slot order;
 *   · its noun phrase appends to S0's list BEFORE the location clause
 *     (location stays terminal), registered in `DISCOVERABILITY_INDEX_NOUNS`.
 * S0 is the one §D1 string that changes on growth, and only by APPENDING a
 * noun phrase; existing nouns are never rewritten. Coverage is lintable:
 * `athleteTeamCopy.test.ts` asserts every capability sentence has a registered
 * noun present in S0. Ceiling: if S0 exceeds 4 rendered lines at 375, CD
 * reopens the pattern.
 *
 * ⚖️ R49 (founder glyph amendment, 2026-08-20): NO EM DASHES on this surface.
 * S1/S3/S5 are re-cut at GLYPH level only (a colon, a comma, a parenthesis —
 * no word, category, claim or order changes); S2/S4 already complied. The
 * frozen-strings rule pins the AMENDED text. New §D1 strings never introduce
 * an em dash; the module lint asserts it over every §D1 constant.
 *
 * ⛔ S1 AND S2 ARE FROZEN (as R49-amended, S2 byte-untouched). They ship
 * character-for-character and are NEVER re-written. ⛔ NEVER concatenate any
 * two slots: each is its own node so the next append touches nothing frozen.
 *
 * ⚖️ SCOPE RULE (ruling 3, S2/S3). The line names categories of the athlete's
 * OWN DATA, using the label the counterpart sees. It does NOT enumerate the
 * coach's derived queue state (`nextRaceInDays`, `plan.reviewDue`,
 * `kudosOpportunity`). ⚠️ COROLLARY: a derived signal that reveals an unnamed
 * CATEGORY is not queue state — S3 applies and opens before that surface flips.
 *
 * ⚖️ MOUNT RULE (CD-PASS3 §1.11b, D4.17; ruling 5's mount half UNCHANGED by
 * R43): ONE LIVE MOUNT satisfies contract §4.8 — under `Coached by {Name}`.
 * ⛔ Do NOT build an athlete Settings → Team card to host this; the obligation
 * attaches automatically only if such a card ships for its own reasons. (A
 * 2026-07-28 lane built exactly that card before reading the ruling; it was
 * WITHDRAWN.) ⛔ NEVER the page footer — that mount is what DS-5 moved it OUT
 * of. Never twice on one surface. ⚠️ Ruling 5's "no expander" CLAUSE is
 * SUPERSEDED-NARROW by R43, for this mount only: the fold is lawful because
 * the resting state (S0 + S2) is itself a complete category-level disclosure.
 *
 * 🔗 S3 · LANDED 2026-08-20 — `DISCOVERABILITY_LOCATION_LINE` below, ruled at
 * the §4.8 CD gate with the MAP LANE (`cd-2026-08-05/CD-RULINGS-2026-08-05.md`
 * §3, ruling 4/R40, superseding the P0-3 rat. 10 pointer). Founder ✓
 * ratified-as-drafted (C25); the CLINICAL pass (contract §4.2) gates the
 * `race_telemetry_r2` FLIP, not the landing — the S5 precedent (2026-08-18):
 * a disclosure may OVER-cover a still-dark capability, never under-cover a
 * live one. If clinical amends the sentence pre-flip, this constant and its
 * pins are the one edit. ⚠️ C26 additionally routes the R49 re-cut wording,
 * rendered in both fold states, through the §4.2 clinical pass BEFORE the
 * presentation change merges to dev.
 *
 * 🔗 STANDING COUPLING (ruling 14 / §7.1). The `Why` this names is the `Why` row
 * of `PlanTable`'s review expander (`getReviewExpanderRows`) — the SAME component
 * on the SAME code path for both readers. Any change to that expander changes what
 * the coach sees and therefore what §D1 must name. Model-authored prose may render
 * on a coach surface ONLY while §D1 names it. S1 still names the `Why` verbatim
 * in the record, and S0's first clause anchors it at the surface.
 */

/**
 * §D1 · S0 — THE INDEX LINE (CD 2026-08-20, R42; candidate A, 👤 founder-ruled
 * C29 — ships non-provisional). Always visible, first in the block, the ONE
 * §D1 string that changes on growth (append-only, location terminal). Every
 * clause is a ratified fragment of its record sentence — S0 authors no new
 * claim language. The trigger renders inline at its tail, inside the same
 * paragraph node.
 */
export const DISCOVERABILITY_INDEX_LINE =
  'Your coach can see how your training is going, your weekly mileage, your runs and the effort you report, and, during a race you choose to share, your live position.';

/**
 * §D1 · THE TRIGGER (CD 2026-08-20, R43; 👤 founder-ruled C29). ONE string —
 * the label never changes with state; `aria-expanded` carries it. The `›` is
 * part of the ledgered string (the render may split it into its own span to
 * rotate, deriving the split from this constant, never hard-coding the text).
 */
export const DISCOVERABILITY_TRIGGER_LABEL = 'The full wording ›';

/**
 * §D1 · THE S0 NOUN REGISTRY (R48). Maps every capability sentence in the
 * record to the S0 noun phrase that names its class at the surface — each
 * phrase a verbatim fragment of S0. `athleteTeamCopy.test.ts` asserts the
 * mapping is total (every capability sentence registered, every registered
 * noun present in S0) so a future S6 cannot land in the record without landing
 * in the index in the same PR.
 *
 * ⛔ S2 (`DISCOVERABILITY_CONSENT_LINE`) is deliberately NOT here: it is the
 * consent fence, not a capability — it never folds, so the index never needs
 * to cover it.
 */
export const DISCOVERABILITY_INDEX_NOUNS: Readonly<Record<string, string>> = {
  DISCOVERABILITY_LINE: 'how your training is going',
  DISCOVERABILITY_LOCATION_LINE:
    'during a race you choose to share, your live position',
  DISCOVERABILITY_MILEAGE_LINE: 'your weekly mileage',
  DISCOVERABILITY_RUNS_LINE: 'your runs and the effort you report',
};

/**
 * §D1 · S1 — THE REGISTER LINE. R49 glyph re-cut 2026-08-20 (the em dash
 * became a colon; no word, category or claim changed). Renders in the fold.
 */
export const DISCOVERABILITY_LINE =
  'Your coach can see how your training is going: Tayro plan alignment, recent activity, your route times and distance bests, and the Why on each planned session.';

/**
 * §D1 · S3 — THE LOCATION SENTENCE (map lane; CD 2026-08-05 §3, ruling 4/R40).
 * Fills the slot reserved by the 2026-08-02 three-slot anatomy — renders
 * between S1 and S4, in slot order, S2 still last.
 *
 * Race-day live tracking (dark behind `race_telemetry_r2`): during a race the
 * athlete chooses to share, her coach can see her live position on the meet
 * map. §D1 grows by APPENDING (S1/S4/S5 byte-untouched, its own node).
 *
 * ⚖️ Sign-off state, precisely (COPY-LEDGER §G): 👤 founder ✓
 * ratified-as-drafted 2026-08-05 (C25). The CLINICAL pass (contract §4.2)
 * runs 2026-08-20 and gates the `race_telemetry_r2` FLIP — shipped dark
 * ahead of it on the S5 precedent: a disclosure may OVER-cover a still-dark
 * capability, never under-cover a live one. If clinical amends the wording,
 * this constant + its pins are the one edit, BEFORE any flip.
 *
 * Register grounds (rulings §3): names her own data with the label she will
 * see — *the meet map* IS the surface the §3.6 preview shows her (standing
 * S3), which is why the module lint's athlete-register rule carries a narrow,
 * named carve for exactly the phrase `meet map` and nothing wider. Claims no
 * control she lacks mid-race (no "any time"); "never outside one" fences
 * training runs, true by the race-scoped grant; no health implication, no
 * verdict.
 *
 * R49 glyph re-cut 2026-08-20 (the em dash became a comma; no word, category
 * or claim changed). The re-cut wording joins the C26 clinical review; renders
 * in the fold.
 */
export const DISCOVERABILITY_LOCATION_LINE =
  'During a race you choose to share, your coach can also see your live position on the meet map, never outside one, and it ends with your run.';

/**
 * §D1 · S4 — THE MILEAGE-SERIES SENTENCE (Rosa R-1, founder-ruled "append"
 * 2026-08-17; at its ship date S3 was still reserved — it landed 2026-08-20).
 *
 * Rosa's scoped actions read an 8-completed-week weekly-volume series and
 * surface it to the coach; §D1 grows by appending (never by rewriting S1/S2),
 * so the new named series gets its own sentence. Register rules honored:
 * names the concrete metric + window, no "progress", no reassurance framing.
 * Disclosure may OVER-cover ("8 weeks" covers "8 completed weeks" and the
 * team-week action's 2-week volume read), never under-cover.
 *
 * Already R49-compliant (no em dash) — byte-untouched by the 2026-08-20
 * presentation pass; renders in the fold.
 */
export const DISCOVERABILITY_MILEAGE_LINE =
  'Your coach can also see your weekly mileage over the last 8 weeks.';

/**
 * §D1 · S5 — THE RUNS-AND-EFFORT SENTENCE (Rosa v1.1 W1+W2; RB-15, founder +
 * CD ratified 2026-08-18; at its ship date S3 was still reserved — it landed
 * 2026-08-20).
 *
 * Rosa's athlete action widens to read the runs behind each week (day-grain
 * date + km ONLY — nothing timed, nothing located; the W2 boundary is law)
 * and the weekly average of reported effort (RPE). §D1 grows by APPENDING
 * (never by rewriting S1/S2/S4), so the newly named categories get their own
 * sentence, shipped in the SAME PR as the slice widening (the RB-7/S4
 * discipline). Register rules honored: names the concrete categories, uses
 * the athlete's own words for them ("the effort you report"), no reassurance
 * framing. Ships with the typographic apostrophe, character-for-character;
 * founder + clinical pass before `rosa_v1_1` flips (a disclosure may
 * OVER-cover a still-dark capability, never under-cover a live one).
 *
 * R49 glyph re-cut 2026-08-20 (the em-dash pair became a parenthesis; no
 * word, category or claim changed). Renders in the fold.
 */
export const DISCOVERABILITY_RUNS_LINE =
  'Your coach can also see the runs behind those weeks (each run’s date and distance) and the effort you report (RPE).';

/**
 * §D1 · S2 — THE CONSENT CLAUSE. Ships character-for-character; frozen.
 *
 * ⚠️ It is nine words and it STANDS ALONE. The informed-consent half is the part
 * the founder's ruling requires, and it cannot ride at the tail of a five-item
 * list where nobody finishes reading (ruling 1).
 *
 * ⚠️ It deliberately does NOT name chat. Onboarding answers shape plans too, and
 * a disclosure may OVER-cover, never under-cover. `what it writes there` is
 * deliberately unbounded: S1 is the anchor that tells her where to look, S2 is
 * the fence that covers the prose wherever else it lands. It also names Tayro as
 * the AUTHOR, which she needs in order to reason about the clause at all.
 *
 * ⛔ It names no health category, so it carries no health-data implication.
 *
 * ⛔ IT NEVER FOLDS (R42, 2026-08-20): the fence stays visible in both fold
 * states and stays LAST IN DOM in both — below the fold region, never inside
 * it. Already R49-compliant; byte-untouched by the presentation pass.
 */
export const DISCOVERABILITY_CONSENT_LINE =
  'What you tell Tayro can shape what it writes there.';

// ── §D1 · THE LOCATION STRATUM (CD 2026-08-21, R59/R60 — the DISC set) ─────
//
// The rat.-10 discoverability block (CD 2026-08-05 R37), placed by R59 as a
// STRATUM below S2 inside the one §D1 mount card. DARK behind
// `race_disc_stratum_v1`. Strings are the CD-RULINGS-2026-08-05 §2 rows
// (C25 ✓ founder), copied character-for-character from that table — the
// COPY-LEDGER's §G names it the character-level source of truth.
//
// ⚠ NAMING IS LOAD-BEARING: none of these constants may start with
// `DISCOVERABILITY_` and end with `_LINE` — that namespace is the R48 noun
// registry's sweep (a capability sentence of the RECORD). The stratum is
// live STATE below the record, not a record sentence; S0 never indexes it.
//
// C35 RULED (founder, 2026-08-21): RE-CUT. The 2026-08-05 em-dash cuts of
// DISC-1/2 collided with R49's no-dash law the moment R59 mounted them on
// the §D1 surface; the founder ruled re-cut over scoped exception at their
// FIRST mount (glyphs changed, words untouched — same substance, R49
// grammar: comma / parentheses). The 2026-08-05 §2 table stays the record
// of the ORIGINAL cuts; these constants are now the characters of record.

/** DISC-1 — LOCATION · Sees (per-coach row). C35 re-cut (em dash → comma). */
export const DISC_SEES =
  'Your dot with your initials, full name on tap. Pace, distance and elevation while you run.';

/** DISC-2 — LOCATION · When (per-coach row). C35 re-cut (em dash → parentheses). */
export const DISC_WHEN =
  'Only during a race you share. It ends with your run (2 hours at most).';

/** DISC-3 — LOCATION · idle. The ENTIRE resting anatomy (R60): one quiet
 *  line, no Sees/When rows, no End button at rest. */
export const DISC_RESTING = 'Nothing is shared right now.';

/** DISC-4 — LOCATION · active. `Sharing live · {Meet} · ends by {time}`.
 *  {Meet} is the coach-authored meet NAME (data, not register). */
export function discActiveLine(meetName: string, endsByLabel: string): string {
  return `Sharing live · ${meetName} · ends by ${endsByLabel}`;
}

/** DISC-5 — LOCATION · revoke. A REAL button through the EXISTING §4.4 kill
 *  (device stops sending first); ending the share never ends the run (R35).
 *  Declared reuse: STAL-4 on the run screen's staleness prompt IS this
 *  string (README "Strings of record"). */
export const DISC_END_SHARING = 'End sharing';

/** The stratum's row kickers (`SEES` / `WHEN` render CSS-uppercased — the
 *  GDN-RAIL double-mount discipline: same characters, case is styling). */
export const DISC_SEES_KICKER = 'Sees';
export const DISC_WHEN_KICKER = 'When';

// ── §2.2 · YOUR SEASON ──────────────────────────────────────────────────────

/** §C5 — season kicker (existing label). */
export const SEASON_KICKER = 'YOUR SEASON';

/** §C6 — season line. Ledgered: `Week {n} of {m} · {Phase}` (existing composition). */
export function seasonLine(weekN: number, weeksTotal: number, phase: string): string {
  return `Week ${weekN} of ${weeksTotal} · ${phase}`;
}

/** §C7 — the phase row's right-hand label. Ledgered: `{NextPhase} next`. */
export function nextPhaseLabel(nextPhase: string): string {
  return `${nextPhase} next`;
}

/** §C8 / E13 — no season yet. Existing string, already in voice, kept verbatim. */
export const SEASON_NONE = 'Season progress lands when your plan is set.';

// ── §2.3 · NEXT RACE ────────────────────────────────────────────────────────

/** §C9 — next-race kicker. */
export const NEXT_RACE_KICKER = 'NEXT RACE';

/** §C10 — the next-race link. ⚠️ DEFECT REPAIR: today this reads `View plan →`
 *  and goes to `/races` (`AthleteSeasonAux.tsx:170`) — the label was lying
 *  about its destination. Fixed by RELABELLING, not by re-targeting. */
export const SEE_RACES_LINK = 'See races ›';

/** E14a — no upcoming race, none ever. */
export const NEXT_RACE_NONE = 'No race on the calendar yet.';

/** E14b — every season race has been run. Keeps `See races ›` as its secondary. */
export const NEXT_RACE_ALL_RUN = 'Season races are done. Results are in Races.';

// ── §2.4 · TEAM KUDOS (rides `athlete_kudos_v1`) ────────────────────────────

/** §C11 — kudos card title. */
export const KUDOS_CARD_TITLE = 'Team kudos';

/** §C12 — kudos overflow link. Ledgered: `See all {n} ›`. Renders only when the
 *  feed holds more than the 3-row cap (D4.15). */
export function kudosSeeAllLabel(n: number): string {
  return `See all ${n} ›`;
}

/** §C13 — the composer pill (D4.1), replacing the always-open 3-field form. */
export const SEND_KUDOS_PILL = 'Send kudos';

/** §C14 — the composer sheet's title. */
export const SEND_KUDOS_SHEET_TITLE = 'Send kudos';

/**
 * §C17a — the pulse header's kudos line. ⚠️ SENT-ONLY (D4.16).
 * Ledgered: `You sent {g} kudos this week.`
 *
 * ⛔ There is no received twin and there must never be one. A received-count is
 * a weekly tally of how much a minor's teammates liked her; being private to
 * her does not fix it, because the number is still there to watch week over
 * week. Nothing is lost — the feed already carries every received kudo as the
 * named, dated event it was (`Amara → you · Strong finish · 2h`).
 */
export function kudosSentLine(g: number): string {
  return `You sent ${g} ${pick(g, 'kudo', 'kudos')} this week.`;
}

/** §C17b — the same line at zero sent. The line NEVER disappears and the block
 *  NEVER reflows (D4.16); the zero state folds into the same one line. */
export const KUDOS_SENT_LINE_ZERO = 'No kudos from you yet this week.';

// ── §2.5 / §2.6 · TEAMMATES + FOOTER ────────────────────────────────────────

/** §C15 — teammates card title (D4.6). Renamed from `Roster` on the ATHLETE
 *  surface only: *roster* is a coach's word for a list she manages, *teammates*
 *  is the athlete's word for the same people. */
export function teammatesCardTitle(n: number): string {
  return `Teammates · ${n}`;
}

/** §C16 — the footer link (D4.7). Existing string, relocated out of the roster
 *  card header so the page's one destructive action is not in prime real
 *  estate. The `ConfirmDialog` flow is unchanged. */
export const LEAVE_TEAM = 'Leave team';

// ── FRAME 5 · the kudos empties (all ride `athlete_kudos_v1`) ───────────────

/** E3 — the team has no kudos. Replaces `KudosFeed`'s vanilla-pinned
 *  `No kudos yet — be the first to send one!`, which breaches the ratified
 *  no-exclamation rule (frame 5 §4, D5.6). */
export const E3_NO_KUDOS = 'No kudos yet. The first one lands here.';

/** E5a — `/team/kudos`, the `Given` filter is empty. */
export const E5A_NONE_GIVEN = "You haven't sent any kudos yet.";

/** E5b — `/team/kudos`, the `Received` filter is empty.
 *  ⛔ Its former hint, `Keep at it — your teammates are watching.`, is DELETED
 *  with no replacement (D5.4 / §B17). It pressures a minor about being
 *  observed, on a page whose other job is telling her that her coach can see
 *  her trends. That is a posture fix, not a style fix — E5b stands alone. */
export const E5B_NONE_RECEIVED = 'No kudos for you yet.';

/** E5 secondary, both variants. */
export const E5_SECONDARY = 'See all team kudos ›';

/** E6 — the lone athlete: no eligible recipients. Renders BENEATH a `Send
 *  kudos` pill that is DISABLED (D5.7), never attached to a composer that
 *  cannot be used. Replaces `RecipientChips`' `No peer athletes yet — your
 *  coach will see when more join.`, which also implied the coach was being
 *  shown something. */
export const E6_LONE_ATHLETE =
  "You're the first one here. Kudos open up when a teammate joins.";

/** E7 — Today's `TeamFeed` card with no activity (D5.5). ⚠️ Lives on TODAY,
 *  not Team; it rides `athlete_kudos_v1` and its `<Link>` is flag-free (it was
 *  already repaired same-day as B-41). */
export const E7_TODAY_NO_KUDOS = 'No team kudos yet.';

/** E7's secondary. */
export const E7_TODAY_LINK = 'Open Team →';

// ── FRAME 5 · E1, the athlete's no-team screen (rides `athlete_team_tab_v1`) ─

/** E1 — kicker. */
export const E1_KICKER = 'YOUR TEAM';

/** E1 — title (Instrument Serif 22). */
export const E1_TITLE = "You're not on a team yet.";

/** E1 — body. ⚠️ This single sentence is where `TeamsHowItWorks`' three-step
 *  rail card goes (cut C10): the ratified rule is ONE empty state per screen,
 *  so the playbook is FOLDED IN, never ported as a second card. */
export const E1_BODY =
  'Paste an invite from your coach, or start a team of your own.';

/**
 * B70 — E1 body for an account the server will not let create a team
 * (`team_create_coach_only_v1` refuses a non-coach `users.role`). PROVISIONAL.
 * The ratified `E1_BODY` above names both CTAs; when the "Start a team ›"
 * secondary is suppressed, the sentence keeps only the promise the page can
 * still keep, rather than pointing at a control that is no longer there.
 */
export const E1_BODY_INVITE_ONLY = 'Paste the invite your coach sent you.';

/** E1 — the one primary pill. Existing CTA behaviour, unchanged. */
export const E1_PRIMARY = 'Paste an invite';

/** E1 — the one quiet secondary. Existing CTA behaviour, unchanged. */
export const E1_SECONDARY = 'Start a team ›';
